import { useCallback, useEffect, useState, VFC } from 'react'
import { Form, FormLayout } from '@panwds/react-form'
import { CardHeader, CardBody, CardFooter, useToast, Card } from '@panwds/react-ui'
import { TSGDropDown, CSPDropDown } from '../activation/components'
import type { CommonStoreState } from '../activation/store'
import type { IDropDownData } from '../activation/components/common/DropDown/DropDown'
import ScreenLoader from '../activation/components/common/ScreenLoader'
import SubscriptionService from '../activation/services/SubscriptionService'
import { updateStateProps } from '../activation/actions'
import FormFooter from './FormFooter'
import TextBox from './TextBox'
import useWindowDimensions from '../common/hooks/useWindowDimensions'
import { useFormContext } from 'react-hook-form'
import { ZTP_ERROR_AND_MESSAGES } from '../common/constants/LabelConstants'
import { CSP_FIELD_NAME, TSG_FIELD_NAME } from '../activation/components/common/GlobalConstants'

type ZTPDeviceActivationContainerProps = {
  state: CommonStoreState,
  notonorafter?: string
}
type ZTPDeviceActivationFormProps = {
  state: CommonStoreState
}
const ZTPDeviceActivationFormFields: VFC<ZTPDeviceActivationFormProps> = ({ state }) => {
  const { formState: { isSubmitted }, setValue } = useFormContext() || {}
  const { tsg, loadingTsg, instances } = state
  const [selectedTSG, setSelectedTSG] = useState<IDropDownData>({ value: '', id: '' })
  const [selectedSupportAccount, setSelectedSupportAccount] = useState<IDropDownData>({ value: '', id: '' })
  const [disableTsgDropDown, setDisableTsgDropDown] = useState<boolean>(false)
  const [disableCspDropDown, setDisableCspDropDown] = useState<boolean>(false)
  const [instance, setInstance] = useState<any>()

  useEffect(() => {
    if (tsg && !loadingTsg) {
      if (instances?.length) {
        setDisableCspDropDown(true)
        setInstance(instances.find((each) => each.region && each.region !== 'global' && each.support_account_id))
      }
      else {
        setDisableCspDropDown(false)
        setInstance(null)
      }
    }
  }, [instances, loadingTsg, tsg])

  useEffect(() => {
    if (state.supportAccounts?.length === 1) {
      setSelectedSupportAccount({ value: state.supportAccounts[0].name, id: `${state.supportAccounts[0].accountid}` })
      setDisableCspDropDown(true)
    }
    else {
      setDisableCspDropDown(false)
    }
    if (state.tsgs.children?.length === 1) {
      setSelectedTSG({ value: state.tsgs.children[0].display_name, id: `${state.tsgs.children[0].id}` })
      setValue(TSG_FIELD_NAME, { value: state.tsgs.children[0].display_name, id: `${state.tsgs.children[0].id}` })
      updateStateProps({ tsg: state?.tsgs?.get(state.tsgs.children[0].id) })
      setDisableTsgDropDown(true)
    }
    else {
      setDisableTsgDropDown(false)
    }
  }, [state.supportAccounts, state.tsgs, setValue])

  useEffect(() => {
    if (isSubmitted) {
      setValue('serial', '')
      setValue('claimKey', '')
    }
  }, [isSubmitted, setValue])

  const onTSGChange = useCallback((value: IDropDownData) => {
    setSelectedTSG(value)
    const tsg_id = value.id || ''
    updateStateProps({ tsg: state?.tsgs?.get(tsg_id) })
  }, [state?.tsgs])

  const onCspChange = useCallback((value: IDropDownData) => {
    setSelectedSupportAccount(value)
  }, [])

  const isNumericValue = (value: string) => {
    const numberRegex = /^\d+$/
    return numberRegex.test(value) || 'Must be numeric.'
  }
  const isAlphaNumeric = (value: string) => {
    const regEx = /^[A-Z0-9]+$/
    return regEx.test(value) || 'Must be alphanumeric and uppercase.'
  }

  const isValidClaimKey = (value: string) => {
    if (!value) return 'Claim Key is required.'
    if (value && value.length !== 8) return 'Claim key must be 8 digits.'
    return isNumericValue(value)
  }

  const isValidSerialNumber = (value: string) => {
    if (!value) return 'Serial Number is required.'
    if (value.length < 10 || value.length > 33) return 'Serial Number must be 10 to 32 characters.'
    return isAlphaNumeric(value)
  }
  return (
    <FormLayout>
      <Card>
        <CardHeader title={'Activate ZTP Firewall'} level={2} />
        <CardBody addClassName='cm-p-8 cm-text-left'>
          {/* cw-grid cw-gap-4 cw-grid-cols-2 */}
          <div className="cm-grid cm-grid-cols-1 cm-gap-2 lg:cm-grid-cols-2 lg:cm-gap-4">
            <div className='cm-max-w-[280px]'>
              <TSGDropDown
                state={state}
                allTsgList={state.tsgs}
                currentTsg={selectedTSG}
                create_new={false}
                customer_support_account={selectedSupportAccount?.id}
                dropDownList={[]}
                showAllTSG={true}
                size={{
                  height: 32,
                  width: 280
                }} display={{
                  errorMessage: 'Select TSG to proceed activation',
                  toolTipMessage: 'Select the TSG where the ZTP ngfw needs to be activated or create new TSG',
                  subTitle: ''
                }} dropDownProps={{
                  required: true,
                  addNewValue: false,
                  triggerValidationOnChange: true
                }} callbacks={{
                  changeHandlerCallback: onTSGChange
                }} passByProps={{
                  enableSearch: true,
                  label: 'Tenant',
                  dataTestId: 'ztp-device-activation-screen',
                  placeholder: 'Select Tenant',
                }} isLoading={state.loadingTsg} disable={disableTsgDropDown} /></div>
            <div>
              <CSPDropDown
                disable={disableCspDropDown}
                size={{
                  height: 32,
                  width: 280
                }} display={{
                  errorMessage: 'Select CSP Account to proceed activation',
                  toolTipMessage: 'Select the CSP Account where the ZTP ngfw needs to be activated.',
                  subTitle: ''
                }} dropDownProps={{
                  required: true,
                  addNewValue: false,
                  triggerValidationOnChange: true,
                }}
                callbacks={{
                  changeHandlerCallback: onCspChange
                }}
                passByProps={{
                  enableSearch: true,
                  label: 'CSP Account',
                  dataTestId: 'ztp-device-activation-screen',
                  placeholder: 'Select CSP Account',
                }}
                isLoading={false}
                curTsgInstCsp={instance?.support_account_id}
                tsg={tsg as any}
                currentTsg={selectedTSG.id ?? ''}
                supportAccounts={state?.supportAccounts} /></div>
            <div>
              <TextBox
                placeholder="Serial Number"
                showLabel={false}
                name='serial'
                label='Serial Number'
                tooltip='This is a 10 to 32 digit number found on the back of the device.'
                validation={isValidSerialNumber}
              />
            </div>
            <div>
              <TextBox
                placeholder='Claim Key'
                name='claimKey'
                showLabel={false}
                label='Claim Key'
                tooltip='This is an 8-digit number found on the back of the device.'
                validation={isValidClaimKey}
              />
            </div>
          </div>
        </CardBody>
        <CardFooter>
          <FormFooter dataTestId='ztp-activate-screen' buttonText='Activate' />
        </CardFooter>
      </Card>
    </FormLayout>
  )
}
const ZTPDeviceActivationForm: VFC<ZTPDeviceActivationContainerProps> = ({ state, notonorafter }) => {
  const { toast } = useToast()
  const { width } = useWindowDimensions()
  const [activationInProgress, setActivationInProgress] = useState(false)
  // const [shouldResetForm, setShouldResetForm] = useState(false)

  useEffect(() => {
    //TODO: This is big hack to support toast in small screens. remove this once pawds toast component supports different positioning.
    const toastwrapperClass = document.querySelector('.tw-absolute')?.classList
    if (width < 1024) {
      toastwrapperClass?.remove('tw-top-0', 'tw-right-0')
      toastwrapperClass?.add('tw-bottom-20', 'tw-left-4/5')
    }
    return () => {
      toastwrapperClass?.add('tw-top-0', 'tw-right-0')
      toastwrapperClass?.remove('tw-bottom-20', 'tw-left-4/5')
    }
  }, [width])

  const getSupportAccountSignature = useCallback((support_account_id) => {
    return state.supportAccounts.find(e => e.accountid === support_account_id)?.signature
  }, [state.supportAccounts])

  //TODO: Move this function in Util,so it can be shared with DefaultActivationForm.tsx
  const calculateNewTsg = useCallback((currentTsgName: string, tsgName: string, tsgDropDownDetails: any, count = 0): string => {
    const isNewTsgName = tsgDropDownDetails.some((each: any) => {
      return each.display_name === tsgName
    })
    if (!isNewTsgName) {
      return tsgName
    }
    else {
      count++
      return calculateNewTsg(currentTsgName, `${currentTsgName} - ${count}`, tsgDropDownDetails, count)
    }
  }, [])

  const onSubmit = async (values: any) => {
    setActivationInProgress(true)
    // setShouldResetForm(false)
    const signature = getSupportAccountSignature(values[CSP_FIELD_NAME].id) || ''
    const x_sig = encodeURI(`${values[CSP_FIELD_NAME].id} ${notonorafter} ${signature}`)
    const payload = {
      'x-sig': x_sig, //TODO: add error handling for empty xsig
      'supportAccountId': `${values[CSP_FIELD_NAME].id}`,
      'devices': [{
        'serial': values.serial,
        'claimKey': values.claimKey
      }]
    }
    SubscriptionService.activateZTPDevice.post(payload, values[TSG_FIELD_NAME].id).then(async (data) => {
      const hasSuccess = data.data[0].success
      const hasAssociation = data.data[0].hasAssociation
      if (hasSuccess) {
        if (!hasAssociation) {
          toast('Warning! Firewall activation successful. But Device Association failed.', { appearance: 'warning' })
        }
        else {
          toast('Success! Firewall activation successful.', { appearance: 'success' })
        }
      }
      else {
        const error_details = data.data[0].error_details || {}
        const message = ZTP_ERROR_AND_MESSAGES[`${error_details?.error_code}`] || error_details.message
        toast(`Failed activation. ${message}`, { appearance: 'error' })
      }
      setActivationInProgress(false)
      // setShouldResetForm(true)
    }).catch((error) => {
      setActivationInProgress(false)
      toast(`Failed activation.${error.message || ''}`, { appearance: 'error' })
      // setShouldResetForm(true)
    })
  }

  return (
    <div className='cm-bg-white'>
      {activationInProgress && <ScreenLoader />}
      <Form onSubmit={onSubmit} mode='onBlur' formProps={{ style: { height: '100%', width: '100%' }, autoComplete: 'off' }}>
        <ZTPDeviceActivationFormFields state={state} />
      </Form>
    </div>
  )
}
export default ZTPDeviceActivationForm
